import { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

export type UserRole = 'admin' | 'superadmin' | 'staff' | 'agent';

export const useUserRoles = () => {
  const { getIdTokenClaims, isAuthenticated } = useAuth0();
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchRoles = async () => {
      if (!isAuthenticated) {
        setIsLoading(false);
        return;
      }

      try {
        const claimsRaw = await getIdTokenClaims();
        const claims = claimsRaw as any;
        const userRoles =
          claims?.['user/roles'] ||
          claims?.roles ||
          claims?.['https://dev-sitk24ss2iowl228.eu.auth0.com/roles'] ||
          [];

        // Normalize role names to lowercase
        const normalizedRoles = (Array.isArray(userRoles) ? userRoles : []).map(
          (role: string) => role.toLowerCase(),
        ) as UserRole[];

        setRoles(normalizedRoles);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching user roles:', error);
        setRoles([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRoles();
  }, [getIdTokenClaims, isAuthenticated]);

  const hasRole = (role: UserRole): boolean => {
    return roles.includes(role);
  };

  const hasAnyRole = (roleList: UserRole[]): boolean => {
    return roleList.some(role => roles.includes(role));
  };

  const isAdmin = (): boolean => {
    return hasAnyRole(['admin', 'superadmin']);
  };

  const isStaff = (): boolean => {
    return hasRole('staff');
  };

  return {
    roles,
    isLoading,
    hasRole,
    hasAnyRole,
    isAdmin,
    isStaff,
  };
};
